import EditIcon from '@mui/icons-material/Edit';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormLabel,
  IconButton,
  useTheme,
} from '@mui/material';
import { TaxonomyTree } from 'interfaces/taxonomy';
import React from 'react';
import { useUpdateTaxonomyMutation } from 'utils/queryHooks/taxonomy/useTaxonomyMutations';

import { Checkbox, Grid, TextField } from '@mui/material';
import Loader from 'components/Loader';

import { ColorPicker } from 'components/atoms/ColorPicker';
import { isNull, omitBy } from 'lodash';
import queryClient from 'utils/queryClient';

interface EditTaxonomyButtonProps {
  taxonomyTree: TaxonomyTree;
}

const EditTaxonomyButton: React.FC<EditTaxonomyButtonProps> = ({ taxonomyTree }) => {
  const theme = useTheme();
  const [dialogOpen, setDialogOpen] = React.useState(false);

  const [isAbstract, setIsAbstract] = React.useState<boolean | null>(null);
  const [path, setPath] = React.useState<string | null>(null);
  const [name, setName] = React.useState<string | null>(null);
  const [defaultColor, setDefaultColor] = React.useState<string | null>(null);

  const updateTaxonomyModel = omitBy(
    {
      isAbstract,
      newPath: path,
      name,
      defaultColor,
    },
    isNull
  );

  const handleOpenDialog = () => {
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);

    // Reset the form fields when closing the dialog
    setIsAbstract(null);
    setPath(null);
    setName(null);
    setDefaultColor(null);
  };

  const mutation = useUpdateTaxonomyMutation(taxonomyTree?.path);

  const handleUpdateTaxonomy = async () => {
    await mutation.mutateAsync(updateTaxonomyModel, {
      onSuccess: () => {
        if (updateTaxonomyModel?.newPath) {
          queryClient.invalidateQueries(['taxonomyTags']);
        }
      },
    });
    setDefaultColor(null);
  };

  return (
    <>
      <IconButton aria-label="Edit" onClick={() => handleOpenDialog()}>
        <EditIcon />
      </IconButton>

      <Dialog open={dialogOpen} onClose={handleCloseDialog}>
        <DialogTitle>Edit Taxonomy</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} direction="column">
            <Grid item>
              <FormControl sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                <FormLabel>is abstract?</FormLabel>
                <Checkbox
                  checked={isAbstract || taxonomyTree?.isAbstract}
                  onChange={(e) => setIsAbstract(e.target.checked)}
                />
              </FormControl>
            </Grid>
            <Grid item>
              <FormControl>
                <FormLabel>Path</FormLabel>
                <TextField fullWidth value={path || taxonomyTree?.path} onChange={(e) => setPath(e.target.value)} />
              </FormControl>
            </Grid>
            <Grid item>
              <FormControl>
                <FormLabel>Name</FormLabel>
                <TextField value={name || taxonomyTree?.name} onChange={(e) => setName(e.target.value)} />
              </FormControl>
            </Grid>
            <Grid item>
              <FormControl>
                <FormLabel>Default Color</FormLabel>
                <ColorPicker
                  currentColor={defaultColor || taxonomyTree?.defaultColor || theme.palette.primary.main}
                  onColorChange={(newColor) => setDefaultColor(newColor)}
                />
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          {mutation.isLoading ? <Loader size="small" /> : <Button onClick={handleUpdateTaxonomy}>Update</Button>}
          <Button onClick={handleCloseDialog}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default EditTaxonomyButton;
